import { defineStore } from 'pinia'
import i18n from '@/i18n'
import router from '@/router'
import { getUserOs, OperatingSystem, isMobile } from '@/utils/misc'
import { getCurrentAppName } from '@/utils/biz/user'
import { getSessionStorage, setSessionStorage } from '@/utils/storage/sessionstorage'

type AnonymousUserToken = {
  user_id: string // 游客 ID
  authorization: string // 游客凭证 JWT
  anonymous: boolean // 是否匿名（是游客），显然是 true
} | null

type RegisteredUserToken = {
  user_id: string // 用户 ID
  email: string // 用户邮箱
  authorization: string // 用户凭证
  anonymous: boolean // 是否匿名（是游客），显然是 false
  is_first: number // 是否新用户：0 否、1 是
  account_access_token: string // 账号体系 token
} | null

type UserInfo = {
  birthday: string
  country: string
  email: string
  nickname: string
  user_id: string
} | null

const useUserStore = defineStore('userStore', {
  state() {
    return {
      ip: '', // 访客或用户的 IP 地址
      os: getUserOs(), // 访客或用户的操作系统
      locale: 'en', // 访客或用户的当前语言（区域）
      // source 用户来源，请使用 getters 中的值
      isMobile: isMobile(), // 访客或用户终端是否为移动端
      anonymousUserToken: <AnonymousUserToken>null, // 匿名用户身份信息（访客）
      registeredUserToken: <RegisteredUserToken>null, // 登录用户身份信息
      userInfo: <UserInfo>null, // 用户信息
      showLogin: false, // 是否显示登录弹框，请在登录组件中监听此变量的变化
      isVideoEnhancerVip: false, // 是否是 Video Enhancer 订阅用户
      isVideoGeneratorVip: false, // 是否是 Video Generator 订阅用户
      isVocalRemoverVip: false, // 是否是 Vocal Remover 订阅用户
      currentFunction: '', // 当前所在项目
    }
  },
  getters: {
    source(): string {
      let source = getSessionStorage('source')
      return source || 'other'
    },
    isAnonymousUser(): boolean {
      return this.registeredUserToken === null && this.anonymousUserToken !== null
    },
    isRegisteredUser(): boolean {
      return this.registeredUserToken !== null
    },
    isNewUser(): boolean {
      return !!this.registeredUserToken && this.registeredUserToken.is_first === 1
    },
    isRtlLang(): boolean { // 是否 right-to-left 语言
      return this.locale === 'ar'
    },
    abGroupName(): string {
      if (this.ip === '') return ''
      let lastDigit = Number(this.ip.split('.').pop())
      if (lastDigit % 2 === 1) {
        return 'A'
      } else {
        return 'B'
      }
    },
    isVip(): boolean {
      const name = router.currentRoute.value.name

      if (name === 'vocalRemoverIndex' || name === 'vocalRemoverEditor') {
        return this.isVocalRemoverVip
      }
      if (name === 'videoEnhancerIndex' || name === 'videoEnhancerEditor') {
        return this.isVideoEnhancerVip
      }
      if (name === 'videoGeneratorIndex' || name === 'videoGeneratorEditor') {
        return this.isVideoGeneratorVip
      }

      return false
    }
  },
  actions: {
    setIp(ip: string) {
      this.ip = ip
    },
    setOs(os: OperatingSystem) {
      this.os = os
    },
    setLocale(locale: string) {
      this.locale = locale
      i18n.global.locale.value = locale as any
      if (this.isRtlLang && !this.isMobile) {
        document.body.style.direction = 'rtl'
      } else {
        document.body.style.direction = ''
      }
    },
    setSource(source: string | undefined) {
      if (source === undefined || source === '') return

      let sourceInSession = getSessionStorage('source')
      if (sourceInSession === source) return

      setSessionStorage('source', source)
    },
    setIsMobile(isMobile: boolean) {
      this.isMobile = isMobile
    },
    setAnonymousUserToken(anonymousUserToken: AnonymousUserToken) {
      this.anonymousUserToken = anonymousUserToken
    },
    setRegisteredUserToken(registeredUserToken: RegisteredUserToken) {
      this.registeredUserToken = registeredUserToken
    },
    setUserInfo(userInfo: UserInfo) {
      this.userInfo = userInfo
    },
    setShowLogin(showLogin: boolean) {
      this.showLogin = showLogin
    },
    setIsVideoEnhancerVip(isVip: boolean) {
      this.isVideoEnhancerVip = isVip
    },
    setIsVideoGeneratorVip(isVip: boolean) {
      this.isVideoGeneratorVip = isVip
    },
    setIsVocalRemoverVip(isVip: boolean) {
      this.isVocalRemoverVip = isVip
    },
    setCurrentFunction(func: string) {
      this.currentFunction = func
    }
  },
  persist: true
})

export default useUserStore